import Typography from '@shared/constants/Typography';
import {
  AlertsColumn,
  PartsHistoryColumn,
} from '@features/repairDetails/RepairDetailsScreenProps';
import {Platform} from 'react-native';

export const repairTabsConst = {
  tasks: 'Tasks',
  parts: 'Parts',
  notes: 'Notes',
  attachments: 'Attachments',
};

export const statusesConst = {
  replace: 'repairDetails.statuses.replace',
  adjusted: 'repairDetails.statuses.adjusted',
  completed: 'repairDetails.statuses.completed',
  na: 'repairDetails.statuses.na',
};

export const repairTabs = [
  {
    text: 'repairDetails.tabs.notes',
    value: repairTabsConst.notes,
  },
  {
    text: 'repairDetails.tabs.parts',
    value: repairTabsConst.parts,
  },
  {
    text: 'repairDetails.tabs.tasks',
    value: repairTabsConst.tasks,
  },
  {
    text: 'repairDetails.tabs.attachments',
    value: repairTabsConst.attachments,
  },
];

export const resolutionStatuses = [
  {
    text: statusesConst.replace,
    value: 'Replaced',
    status: 1,
  },
  {
    text: statusesConst.adjusted,
    value: 'Adjusted',
    status: 2,
  },
  {
    text: statusesConst.completed,
    value: 'Completed',
    status: 3,
  },
  {
    text: statusesConst.na,
    value: 'NA',
    status: 4,
  },
];

export const listViewColumns = [
  {
    key: 'partNumber',
    label: 'repairDetails.parts.list.partNumber',
    flex: 1.8,
    width: Typography.width.width120,
    sortKey: 'partNumber',
  },
  {
    key: 'partName',
    label: 'repairDetails.parts.list.partName',
    flex: 2,
    width: Typography.width.width120,
    sortKey: 'partName',
  },
  {
    key: 'storeRoomDisplayLabel',
    label: 'repairDetails.parts.list.storeRoom',
    flex: 2,
    width: Typography.width.width100,
    sortKey: 'storeRoom',
  },
  {
    key: 'availableQty',
    label: 'repairDetails.parts.list.qty',
    flex: 2,
    width: Typography.width.width120,
    sortKey: 'availableQty',
  },
  {
    key: 'qtyRequired',
    label: 'repairDetails.parts.list.qtyRequired',
    flex: 1.5,
    width: Typography.width.width120,
  },
];

export const filteredListViewColumns = [
  {
    key: 'search',
    label: 'repairDetails.parts.filteredPartList.searchResults',
    flex: 1.8,
  },
];

export const noteTypes = {
  all: 'ALL',
  workOrder: 'WORK_ORDER',
  repair: 'REPAIR',
};

export const notesView = {
  add: 'Add',
  view: 'View',
  edit: 'Edit',
};

export const MAX_SUBJECT_LENGTH = 60;
export const MAX_SUMMARY_LENGTH = 2000;
export const MAX_REQUEST_COMMENT_LENGTH = 512;
export const MIN_QTY = 0.001;
export const MAX_QTY = 9999999.999;

/**
 * Clamps a raw string quantity value to [MIN_QTY, MAX_QTY] and returns it
 * formatted to 3 decimal places.
 * Returns MIN_QTY.toFixed(3) when the input is empty or not a valid number.
 *
 * Use this in place of any inline MIN_QTY / MAX_QTY clamp logic to avoid
 * duplicated code across parts-related screens.
 */
export const clampQtyToString = (rawVal: string): string => {
  if (!rawVal.trim() || Number.isNaN(Number.parseFloat(rawVal))) {
    return MIN_QTY.toFixed(3);
  }
  const parsed = Number.parseFloat(rawVal);
  const clamped = Math.min(Math.max(parsed, MIN_QTY), MAX_QTY);
  return clamped.toFixed(3);
};
export const MAX_ASSET_CHARS = 24;
export const MAX_TASK_COMMENT_CHARS = 500;

export const workOrderDetails = [
  {
    key: 'repairCode',
    value: 'repairDetails.confirmPartsRequest.rpairId',
  },
  {
    key: 'selectedWOId',
    value: 'repairDetails.confirmPartsRequest.workOrderId',
  },
  {
    key: 'selectedTechnicianId',
    value: 'repairDetails.confirmPartsRequest.technicianId',
  },
];

export const quantiyAvailablites = {
  stock: 'In Stock',
  outOfStock: 'Out of Stock',
};

/** Expected error messages for begin shift */
export const definedBeginShiftErrorMessages = {
  woThreshold:
    'Work Order spending limit has been reached. Authorization is required to continue',
  technicianOvertime: 'Technician is in overtime.',
  labourOvertime: 'An overtime rate will be applied for these hours',
  workorderStatusCheck:
    'The work order you selected is not in an Active status',
  /** Message returned by the API after labour overtime is acknowledged for a repair.
   *  Requires a Yes / No confirmation from the technician. */
  technicianOvertimeContinue: 'You are starting this repair during overtime hours. Do you want to continue?',
  /** Message returned by the API when an indirect activity is started during overtime.
   *  Requires a Yes / No confirmation from the technician. */
  indirectActivityOvertimeContinue: 'You are starting this indirect activity during overtime hours. Do you want to continue?',
  endShiftOverTime: 'The current activity you are ending indicates occurred during overtime hours. An overtime rate will be applied for the time spent in overtime',
  enShiftOverTimeSpent: 'An overtime rate will be applied for the time spent in overtime',
  /**
   * Returned by endRepair when this is the last open repair on the work order.
   * Shows a modal asking the user whether to update the WO status.
   * "Finished – Ready for Pickup[F]" → isCheckWorkOrderStatusRequired=true, proceedToChangeWorkOrderStatus=true
   * "Keep Current Work Order Status" → isCheckWorkOrderStatusRequired=false, proceedToChangeWorkOrderStatus=false
   * Note: uses a partial match to handle API responses with or without a space
   * after the period (e.g. "...work order.Do you..." vs "...work order. Do you...").
   */
  lastRepairOnWorkOrder: 'This is the last repair on the work order',
};

export const historyTabs = {
  all: 'All',
  pending: 'pending',
  issued: 'issued',
  ready: 'ready',
  approved: 'approved',
  rejected: 'rejected',
  requested: 'requested',
};

export const partsHistoryTabs = [
  {label: 'repairDetails.partsHistory.all', value: 'All'},
  {label: 'repairDetails.partsHistory.pending', value: 'Pending'},
  {label: 'repairDetails.partsHistory.requested', value: 'Requested'},
  {label: 'repairDetails.partsHistory.approved', value: 'Approved'},
  {label: 'repairDetails.partsHistory.rejected', value: 'Rejected'},
  {label: 'repairDetails.partsHistory.issued', value: 'Issued'},
  {label: 'repairDetails.partsHistory.ready', value: 'Ready'},
];

/** Default status shown on first open */
export const DEFAULT_STATUS = 'Requested';

export const partsHistoryColumns: PartsHistoryColumn[] = [
  {
    key: 'partNumber',
    label: 'repairDetails.partsHistory.partNumber',
    width: Typography.width.width120,
    align: Typography.textAlign.leftAlign,
  },
  {
    key: 'partName',
    label: 'repairDetails.partsHistory.partName',
    width: Typography.width.width120,
    align: Typography.textAlign.leftAlign,
  },
  {
    key: 'partDesc',
    label: 'dashboard.workOrders.partsGrids.partsDesc',
    width: Typography.width.width120,
    align: Typography.textAlign.leftAlign,
  },
  {
    key: 'requestComment',
    label: 'dashboard.workOrders.partsGrids.requestComment',
    width: Typography.width.width100,
    align: Typography.textAlign.leftAlign,
  },
  {
    key: 'requestedQty',
    label: 'repairDetails.partsHistory.requestedQuantity',
    width: Typography.width.width100,
    align: Typography.textAlign.leftAlign,
  },
  {
    key: 'issuedQty',
    label: 'repairDetails.partsHistory.issuedQuantity',
    width: Typography.width.width120,
    align: Typography.textAlign.leftAlign,
  },
  {
    key: 'requestPartStatus',
    label: 'repairDetails.partsHistory.status',
    width: Typography.width.width120,
    align: Typography.textAlign.leftAlign,
  },
  {
    key: 'createdDate',
    label: 'repairDetails.partsHistory.requestedDate',
    width: Typography.width.width140,
    align: Typography.textAlign.leftAlign,
  },
  {
    key: 'firstName',
    label: 'repairDetails.partsHistory.requestedBy',
    width: Typography.width.width120,
    align: Typography.textAlign.leftAlign,
  },
];

export const attachmentTypes = {
  all: 'ALL',
  workOrder: 'WORK_ORDER',
  asset: 'ASSET',
};

export const attachmentView = {
  add: 'Add',
  view: 'View',
  edit: 'Edit',
};

export const attachmentViewColumns = [
  {
    key: 'fileName',
    label: 'repairDetails.attachments.list.name',
    flex: 3,
    width: Typography.width.width252,
    sortKey: 'fileNameWithExtension',
  },
  {
    key: 'category',
    label: 'repairDetails.attachments.list.attachedTo',
    flex: 2.5,
    width: Typography.width.width187,
  },
  {
    key: 'userName',
    label: 'repairDetails.attachments.list.attachedBy',
    flex: 2.5,
    width: Typography.width.width187,
    sortKey: 'postedBy',
  },
  {
    key: 'modifiedDate',
    label: 'repairDetails.attachments.list.attachedOn',
    flex: 2.5,
    width: Typography.width.width187,
    sortKey: 'modifiedDate',
  },
  {
    key: 'fileSize',
    label: 'repairDetails.attachments.list.attachmentSize',
    flex: 2.5,
    width: Typography.width.width187,
    sortKey: 'fileSize',
  },
  {
    key: 'actions',
    label: 'repairDetails.attachments.list.actions',
    flex: 2,
    width: Typography.width.width200,
    //itemCenterAlign: true,
  },
];

/**
 * Maximum permitted file size for attachment uploads: 3 MiB (binary megabytes).
 *
 * Breakdown of `3 * 1024 * 1024`:
 *   3          — the size limit in mebibytes (MiB)
 *   × 1024     — 1st factor: 1 KiB = 1 024 bytes  → converts MiB → KiB
 *   × 1024     — 2nd factor: 1 MiB = 1 024 KiB    → converts KiB → bytes
 *   ─────────────────────────────────────────────
 *   = 3 × 1 024 × 1 024 = 3 145 728 bytes
 *
 * Windows Explorer reports file sizes in binary units but labels them "MB"
 * (e.g. a file that is 3 × 1 048 576 bytes is displayed as "3 MB" on Windows).
 * Using the binary (MiB) threshold ensures that a file the user sees as "3 MB"
 * in Windows is accepted rather than falsely rejected.
 */
export const MAX_FILE_SIZE_BYTES: number = 3 * 1024 * 1024; // 3 MiB = 3 145 728 bytes
export const MAX_FILENAME_LENGTH = 100;
export const MAX_KEYWORDS_LENGTH = 80;
export const MAX_DESCRIPTION_LENGTH = 500;
export const MAX_ATTACHMENT_DESC_LENGTH = 80;

/** Allowed file extensions for attachment uploads (lowercase, no leading dot). No video types. */
export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  'prn',
  'aspx',
  'bin',
  'doc',
  'docx',
  'gif',
  'jpeg',
  'jpg',
  'pdf',
  'png',
  'swf',
  'xls',
  'xlsx',
];

/**
 * Returns true when the given filename has an allowed attachment extension.
 * Comparison is case-insensitive.
 */
export const isAllowedAttachmentFile = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  return ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext);
};

export const messageFilter = {
  all: 'All',
  read: 'Read',
  unRead: 'UnRead',
};

export const messageColumnKeys = {
  isRead: 'isRead',
  actions: 'actions',
  createdDate: 'createdDate',
  messageSubject: 'messageSubject',
  messageBody: 'messageBody',
  assetNumber: 'assetNumber',
  documentID: 'documentID',
  fullName: 'fullName',
  notificationCount: 'notificationCount',
};

export const messageStatusText = {
  read: 'Read',
  unread: 'Unread',
  view: 'View',
};

export const messageFilterLabels = {
  all: 'repairDetails.messages.filter.all',
  unread: 'repairDetails.messages.filter.unread',
  read: 'repairDetails.messages.filter.read',
};

export const messageViewColumns = [
  {
    key: 'assetNumber',
    label: 'repairDetails.messages.list.asset',
    flex: 1.2,
    width:
      Platform.OS === 'ios'
        ? Typography.width.width100
        : Typography.width.width160,
  },
  {
    key: 'messageSubject',
    label: 'repairDetails.messages.list.subject',
    flex: 1.8,
    width:
      Platform.OS === 'ios'
        ? Typography.width.width160
        : Typography.width.width320,
  },
  {
    key: 'messageBody',
    label: 'repairDetails.messages.list.message',
    flex: 2,
    width:
      Platform.OS === 'ios'
        ? Typography.width.width160
        : Typography.width.width320,
  },
  {
    key: 'documentID',
    label: 'repairDetails.messages.list.woNumber',
    flex: 1,
    width:
      Platform.OS === 'ios'
        ? Typography.width.width120
        : Typography.width.width213,
  },
  {
    key: 'fullName',
    label: 'repairDetails.messages.list.sentBy',
    flex: 1.2,
    width:
      Platform.OS === 'ios'
        ? Typography.width.width140
        : Typography.width.width220,
  },
  {
    key: 'createdDate',
    label: 'repairDetails.messages.list.dateTime',
    flex: 1.2,
    width:
      Platform.OS === 'ios'
        ? Typography.width.width154
        : Typography.width.width252,
  },
  {
    key: 'notificationCount',
    label: 'repairDetails.messages.list.notificationCount',
    flex: 1,
    width:
      Platform.OS === 'ios'
        ? Typography.width.width160
        : Typography.width.width270,
    itemCenterAlign: true,
  },
  {
    key: 'isRead',
    label: 'repairDetails.messages.list.status',
    flex: 0.9,
    width:
      Platform.OS === 'ios'
        ? Typography.width.width100
        : Typography.width.width200,
    itemCenterAlign: true,
  },
  {
    key: 'actions',
    label: 'repairDetails.messages.list.actions',
    flex: 0.8,
    width:
      Platform.OS === 'ios'
        ? Typography.width.width100
        : Typography.width.width200,
    itemCenterAlign: true,
  },
];

export interface FilterTab {
  label: string;
  value: string;
  count: number;
}

export interface MessagesViewProps {
  /**
   * The tab to open on — the `messageFilter` value Nova AI named ("All",
   * "Read", "UnRead"). Absent means the screen's own default, Unread.
   */
  initialFilter?: string;
  repairId?: number;
  fromDashboard?: boolean;
}

export const DEFAULT_PAGE_SIZE = 9;

export const WO_CONFIRMATION_MESSAGE =
  'Confirmation required. The open repair will be set to complete';

export const INT32_MAX = 2_147_483_647; // 2^31 − 1

/** API success message for meter reading update */
export const METER_READING_UPDATED_MESSAGE: string[] = [
  'Meter reading updated successfully.',
  'Reading accepted as invalid.',
  'Reading overridden and saved.',
];

/** API message for invalid meter reading (shown as inline error) */
export const METER_INVALID_READING_MESSAGE = 'Invalid meter reading';

/** API messages that trigger the validation ConfirmModal for meter readings */
export const METER_VALIDATION_MESSAGES: string[] = [
  'Reading must be after acquired date.',
  'A reading already exists for this meter on the same date and time.',
  'Reading is less than the previous reading.',
  'Reading exceeds the next reading.',
  'Transaction date cannot be in the future.',
  'This meter is no longer active.',
];

/**
 * Subset of METER_VALIDATION_MESSAGES for which the ConfirmModal must ALWAYS
 * show the "Accept as Invalid" button, regardless of the canAcceptAsInvalid
 * flag returned by the API.
 */
export const METER_FORCE_ACCEPT_AS_INVALID_MESSAGES: string[] = [
  'Reading plus offset is less than the acquire reading.',
  'Reading exceeds the maximum number of digits for this meter.',
  'Reading exceeds the maximum meter limit.',
];

// ---------------------------------------------------------------------------
// Alerts modal columns
// ---------------------------------------------------------------------------

export const alertsColumns: AlertsColumn[] = [
  {
    key: 'alert',
    label: 'repairDetails.alerts.alert',
    width: Typography.width.width144,
    align: Typography.textAlign.leftAlign,
    itemCenterAlign: false,
    sortKey: 'alert',
  },
  {
    key: 'description',
    label: 'repairDetails.alerts.description',
    width: Typography.width.width252,
    align: Typography.textAlign.leftAlign,
    itemCenterAlign: false,
    // sortKey intentionally omitted — description column is not sortable
  },
  {
    key: 'firstOccurrenceDate',
    label: 'repairDetails.alerts.firstOccurrenceDate',
    width: Typography.width.width210,
    align: Typography.textAlign.leftAlign,
    itemCenterAlign: false,
    sortKey: 'firstOccurrenceDate',
  },
  {
    key: 'lastOccurrenceDate',
    label: 'repairDetails.alerts.lastOccurrenceDate',
    width: Typography.width.width210,
    align: Typography.textAlign.leftAlign,
    itemCenterAlign: false,
    sortKey: 'lastOccurrenceDate',
  },
  {
    key: 'occurrenceCount',
    label: 'repairDetails.alerts.count',
    width: Typography.width.width153,
    align: Typography.textAlign.centerAlign,
    itemCenterAlign: true,
    sortKey: 'occurrenceCount',
  },
];

export const permissions ={
  pm: 'PM',
  sa: 'SA'
}

// ─── Confirm Parts Request Modal ─────────────────────────────────────────────
export const CONFIRM_PARTS_CARD_FIELDS = [
  {key: 'partName'},
  {key: 'partDetails'},
  {key: 'separator'},
  {key: 'requestedQty'},
] as const;

export type ConfirmPartsCardFieldKey = typeof CONFIRM_PARTS_CARD_FIELDS[number]['key'];

// ─── Search Parts Request Modal ───────────────────────────────────────────────

export const SEARCH_PARTS_TAB_ALL = 'ALL';
export const SEARCH_PARTS_TAB_PREVIOUSLY_ISSUED = 'PREVIOUSLY_ISSUED';

export const SEARCH_PARTS_SORTABLE_COLS = [
  'partNumber',
  'partName',
  'assetId',
  'storeroom',
  'availableQty',
] as const;

export type SearchPartsSortableCol = typeof SEARCH_PARTS_SORTABLE_COLS[number];

export const SEARCH_PARTS_TABLE_COLUMNS: {
  key: SearchPartsSortableCol | 'qty';
  labelKey: string;
  sortable: boolean;
}[] = [
  {key: 'partNumber',   labelKey: 'repairDetails.searchPartsModal.colPartNumber',  sortable: true},
  {key: 'partName',     labelKey: 'repairDetails.searchPartsModal.colPartName',    sortable: true},
  {key: 'assetId',      labelKey: 'repairDetails.searchPartsModal.colAssetId',     sortable: true},
  {key: 'storeroom',    labelKey: 'repairDetails.searchPartsModal.colStoreroom',   sortable: true},
  {key: 'availableQty', labelKey: 'repairDetails.searchPartsModal.colAvailableQty',sortable: true},
  {key: 'qty',          labelKey: 'repairDetails.searchPartsModal.colQty',         sortable: false},
];
